/**
 * Simulive Page
 *
 * Displays games with the simulatedLive flag enabled in a data table.
 * Allows starting and stopping simulated live playback.
 */

import { useMemo, useState } from 'react';
import { post, get } from '@truefit/http-utils';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  Checkbox,
  FormGroup,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { MonitorPlay, Pause, Play } from 'lucide-react';

import AppPage from '@/shared/components/PageComponents/AppPage';
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle';
import { useSimuliveGamesQuery } from '../hooks/useSimuliveGamesQuery';
import {
  isReplayActive,
  useReingestStatusQuery,
} from '../hooks/useReingestStatusQuery';
import TestLabSection from './TestLabSection';
import SimuliveSignalFlow from './SimuliveSignalFlow';
import { ROW_ACTIONS_SX, TOOLBAR_ACTIONS_SX } from '@/shared/constants/tableIconButtons';
import GameMatchup from '@/shared/components/GameMatchup';
import StatusBadge from '@/shared/components/StatusBadge';
import DateTimeCell from '@/shared/components/DateTimeCell';
import { getGameStatusColor } from '@/shared/utils/getGameStatusColor';
import { paletteHue } from '@/shared/utils/paletteHues';
import { cx } from '@/styles/muiTheme';
import PageHeader from '@/shared/components/PageHeader';

const REPLAY_STATUS_COLORS: Record<
  string,
  'default' | 'info' | 'success' | 'warning' | 'error'
> = {
  not_started: 'default',
  stopped: 'default',
  queued: 'warning',
  tm_launching: 'warning',
  stopping: 'warning',
  tm_ready: 'info',
  ingesting: 'success',
  failed: 'error',
};

// Transitional states, plus ingesting — work is actually happening, so they get
// the same pulse a live game and an exporting clip use.
const REPLAY_IN_FLIGHT = new Set(['queued', 'tm_launching', 'stopping', 'ingesting']);

interface SimuliveGame {
  id: number;
  uniqueId: string;
  name: string;
  startTimestamp: string | Date;
  awayTeamId: number;
  homeTeamId: number;
  status?: string;
  simulatedLive: boolean;
  simulatedLiveStartTime?: string | Date;
}

interface GameCamera {
  id: number;
  cameraId: number | null;
  gameId: number;
  displayName?: string;
  camIndex: number;
}

/**
 * The API keys feeds by camIndex (0-based), not by the camera row id — it maps
 * camIndex to the TM port. Cameras without one can't be launched.
 */
interface CameraOption {
  camIndex: number;
  label: string;
}

/** Live replay state for one row. Polls; several rows share one request. */
function ReplayStatusCell({ gameId }: { gameId: number }) {
  const { data, isLoading } = useReingestStatusQuery(gameId);

  if (isLoading) return <CircularProgress size={14} />;

  const status = data?.status || 'not_started';
  const detail = [
    data?.stackCount ? `${data.stackCount} stacks` : null,
    data?.instanceId ? `instance ${data.instanceId}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const badge = (
    <StatusBadge
      label={status.replace(/_/g, ' ')}
      color={paletteHue(REPLAY_STATUS_COLORS[status])}
      pulse={REPLAY_IN_FLIGHT.has(status)}
    />
  );

  // StatusBadge is a plain function component, so it cannot hold the ref a
  // Tooltip hands its child — the span is the anchor. Chip forwarded refs
  // itself, which is why this was not needed before.
  return detail ? (
    <Tooltip title={detail}>
      <Box component="span" sx={{ display: 'inline-flex' }}>
        {badge}
      </Box>
    </Tooltip>
  ) : (
    badge
  );
}

/**
 * A game's replay drawn end to end: the recording in S3, the gstreamer box
 * reading it, the TM stack per selected camera, and where each one writes.
 */
function ReplayDetailPanel({ gameId }: { gameId: number }) {
  const { data, isLoading, error } = useReingestStatusQuery(gameId);

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <CircularProgress size={18} />
      </Box>
    );
  }

  // Not folded into the loading branch: a failed read leaves `data` undefined
  // with `isLoading` false, and a spinner there would turn a dead request into
  // an expanding row that never resolves.
  if (!data) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Failed to load replay status
          {error ? `: ${(error as Error).message}` : ''}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {data.note && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {data.note}
        </Alert>
      )}

      {/* The set id has no node of its own — it identifies the whole launch
          rather than any one stage — so it stays a caption above the diagram. */}
      {data.tmSetId && (
        <Typography
          variant="caption"
          sx={{ display: 'block', color: 'text.secondary', mb: 1.5 }}
        >
          TM set {data.tmSetId}
        </Typography>
      )}

      <SimuliveSignalFlow status={data} />
    </Box>
  );
}

/** Start or Stop, chosen from the replay's actual state rather than the game row. */
function ReplayActionsCell({
  gameId,
  onStart,
  onStop,
}: {
  gameId: number;
  onStart: () => void;
  onStop: () => void;
}) {
  const { data } = useReingestStatusQuery(gameId);
  const active = isReplayActive(data?.status);

  // No Stack wrapper — the actions cell supplies ROW_ACTIONS_SX, which owns the
  // layout and the 28px muted button sizing for the whole group.
  //
  // lucide icons render a plain <svg>, not MuiSvgIcon, so ROW_ACTIONS_SX's
  // `& .MuiSvgIcon-root { fontSize: 16 }` does not reach them. Hence `size={16}`
  // at each call site to match the MUI icons in the other tables.
  return active ? (
    <Tooltip title="Stop replay" arrow>
      <IconButton onClick={onStop}>
        <Pause size={16} />
      </IconButton>
    </Tooltip>
  ) : (
    <Tooltip title="Start replay" arrow>
      <IconButton onClick={onStart}>
        <Play size={16} />
      </IconButton>
    </Tooltip>
  );
}

export default function SimulivePage() {
  const title = 'Simulive and Test lab';
  useDocumentTitle(title);

  const queryClient = useQueryClient();
  // isPending rather than isLoading: the query is disabled until Cognito
  // authenticates, and a disabled query reports isLoading false with no data —
  // which would render the "no games" empty state while auth is still resolving.
  const { data, isPending, error } = useSimuliveGamesQuery();
  const [selectedGame, setSelectedGame] = useState<SimuliveGame | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState<'start' | 'stop'>('start');
  const [selectedCameras, setSelectedCameras] = useState<number[]>([]);
  const [includePgm, setIncludePgm] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [cameraOptions, setCameraOptions] = useState<CameraOption[]>([]);

  const games: SimuliveGame[] = useMemo(() => {
    return data?.games ?? [];
  }, [data?.games]);

  const handleStartSimulation = async (game: SimuliveGame) => {
    setSelectedGame(game);
    setDialogAction('start');
    setSelectedCameras([]);
    setIncludePgm(true);
    setActionError(null);
    setCameraOptions([]);

    try {
      const { data } = await get<{ gameCameras: GameCamera[] }>(
        `/v1.0/games/${game.uniqueId}`
      );
      const options = (data.gameCameras || [])
        .filter((c) => typeof c.camIndex === 'number')
        .map((c) => ({
          camIndex: c.camIndex,
          label: c.displayName || `Camera ${c.camIndex + 1}`,
        }))
        .sort((a, b) => a.camIndex - b.camIndex);

      setCameraOptions(options);
      setSelectedCameras(options.map((o) => o.camIndex));
    } catch (err) {
      console.error('Failed to fetch game cameras:', err);
      setActionError('Failed to load cameras for this game');
    }

    setDialogOpen(true);
  };

  const handleStopSimulation = (game: SimuliveGame) => {
    setSelectedGame(game);
    setDialogAction('stop');
    setActionError(null);
    setDialogOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedGame) return;

    if (dialogAction === 'start' && selectedCameras.length === 0 && !includePgm) {
      setActionError('Please select at least one camera');
      return;
    }

    setActionLoading(true);
    setActionError(null);

    try {
      const endpoint =
        dialogAction === 'start'
          ? `/v1.0/games/${selectedGame.id}/reingest/simulive/start-stacks`
          : `/v1.0/games/${selectedGame.id}/reingest/simulive/stop`;

      const payload =
        dialogAction === 'start'
          ? {
              cameras: selectedCameras,
              pgm: includePgm,
            }
          : {};

      await post(endpoint, payload);

      // Both endpoints only queue work; the status column picks up progress.
      queryClient.invalidateQueries({ queryKey: ['simuliveStatus'] });

      setDialogOpen(false);
      setSelectedGame(null);
      setSelectedCameras([]);
      setIncludePgm(false);
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setActionLoading(false);
    }
  };

  const columns = useMemo<MRT_ColumnDef<SimuliveGame>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'Game ID',
        // 100 truncated the header to "GA…" — the uppercase label needs room for
        // the sort arrow and column-actions button beside it, not just the value.
        size: 130,
        muiTableBodyCellProps: { sx: { color: cx.text.muted } },
      },
      {
        accessorKey: 'name',
        header: 'Game Name',
        // Wider than the old raw-string cell: the matchup lays out two logos,
        // two abbreviations and the team-color edge borders.
        size: 320,
        Cell: ({ row }) => <GameMatchup game={row.original} />,
      },
      {
        accessorKey: 'startTimestamp',
        header: 'Start Time',
        // Narrower than the old single-line 'MMM dd, yyyy HH:mm:ss'; the header
        // plus its sort arrow and menu button is what sets the floor now.
        size: 150,
        Cell: ({ row }) => <DateTimeCell value={row.original.startTimestamp} />,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 120,
        Cell: ({ row }) => {
          const status = row.original.status;
          if (!status) return '-';
          return (
            <StatusBadge
              label={status}
              color={getGameStatusColor(status)}
              // This table polls every 15s specifically to catch the scheduler
              // flipping recorded -> live mid-replay; the pulse is what makes
              // that arrive as a visible change rather than a silent one.
              pulse={status === 'live'}
            />
          );
        },
      },
      {
        id: 'replay',
        header: 'Replay',
        size: 150,
        Cell: ({ row }) => <ReplayStatusCell gameId={row.original.id} />,
      },
      {
        id: 'actions',
        header: 'Actions',
        // Was 340 for two text buttons; two 28px icon targets need far less, so
        // the header and its chrome set the floor.
        size: 120,
        Cell: ({ row }) => (
          <Box sx={ROW_ACTIONS_SX}>
            <ReplayActionsCell
              gameId={row.original.id}
              onStart={() => handleStartSimulation(row.original)}
              onStop={() => handleStopSimulation(row.original)}
            />
            {/* The tooltip now carries the whole label, so it keeps the detail
                the old button text could not: that this opens a new tab and
                leaves the monitoring table intact. */}
            <Tooltip title="Open player in a new tab" arrow>
              <IconButton
                onClick={() => window.open(`/games/${row.original.uniqueId}`, '_blank')}
              >
                <MonitorPlay size={16} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [],
  );

  return (
    <AppPage title={title} requiredPermissions={['menu:games:simulive']}>
      {/* xl to match Game Management and Clip Management. At lg the table's
          columns total ~1270px against a 1200px container, which is what forced
          the horizontal scrollbar. */}
      <Container maxWidth="xl" sx={{ marginTop: 4, marginBottom: 4 }}>
        <PageHeader title="Simulive Games" subtitle="View and manage games with simulated live playback enabled" />

        {/* Loading state */}
        {isPending && (
          <Stack sx={{ alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
            <CircularProgress />
          </Stack>
        )}

        {/* Error state */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load simulive games: {(error as any)?.message}
          </Alert>
        )}

        {/* No games state */}
        {!isPending && games.length === 0 && (
          <Alert severity="info">No games with simulatedLive flag enabled</Alert>
        )}

        {/* Data table */}
        {!isPending && games.length > 0 && (
          <Box sx={{ width: '100%' }}>
            <MaterialReactTable
              columns={columns}
              data={games}
              // No enableColumnResizing, and therefore no layoutMode: MRT falls
              // back to 'semantic', a real <table>. That is what Clip
              // Management, Manage Users and Game Management all use, and it is
              // the reason their rows and cells space the way they do — grid
              // mode renders rows and cells as flex boxes instead, with a
              // different padding and alignment model.
              //
              // Semantic also stretches columns to fill the container for free,
              // which is what 'grid' was previously here to approximate for the
              // matchup cell. Column `size` values become hints rather than
              // fixed widths. The tradeoff is losing the resize handles.
              renderDetailPanel={({ row }) => (
                <ReplayDetailPanel gameId={row.original.id} />
              )}
              enablePagination={games.length > 10}
              muiTopToolbarProps={{ sx: TOOLBAR_ACTIONS_SX }}
              initialState={{
                pagination: { pageIndex: 0, pageSize: 10 },
                sorting: [{ id: 'startTimestamp', desc: true }],
              }}
            />
          </Box>
        )}

        <TestLabSection />
      </Container>

      {/* Confirmation Dialog */}
      <Dialog open={dialogOpen} onClose={() => !actionLoading && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogAction === 'start' ? 'Start TM Stacks' : 'Stop TM Stacks'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2}>
            <Typography>
              {dialogAction === 'start'
                ? `Select cameras to launch TiledMedia stacks for game ${selectedGame?.id}`
                : `Stop all TiledMedia stacks for game ${selectedGame?.id}?`}
            </Typography>

            {dialogAction === 'start' && (
              <>
                <FormControl component="fieldset" fullWidth>
            
                  <FormGroup>
                    {cameraOptions.length > 0 ? (
                      cameraOptions.map((option) => (
                        <FormControlLabel
                          key={option.camIndex}
                          control={
                            <Checkbox
                              checked={selectedCameras.includes(option.camIndex)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedCameras([...selectedCameras, option.camIndex]);
                                } else {
                                  setSelectedCameras(
                                    selectedCameras.filter((c) => c !== option.camIndex)
                                  );
                                }
                              }}
                            />
                          }
                          label={option.label}
                          sx={{ fontSize: '0.9rem' }}
                        />
                      ))
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        No cameras configured for this game
                      </Typography>
                    )}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={includePgm}
                          onChange={(e) => setIncludePgm(e.target.checked)}
                        />
                      }
                      label="Program/Main Feed (PGM)"
                      sx={{ fontSize: '0.9rem' }}
                    />
                  </FormGroup>
                </FormControl>
              </>
            )}

            {actionError && (
              <Alert severity="error">{actionError}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color={dialogAction === 'start' ? 'primary' : 'error'}
            disabled={
              actionLoading ||
              (dialogAction === 'start' && selectedCameras.length === 0 && !includePgm)
            }
          >
            {actionLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            {dialogAction === 'start' ? 'Launch' : 'Stop'}
          </Button>
        </DialogActions>
      </Dialog>
    </AppPage>
  );
}
